import { createApiService, type ApiError } from './api';
import type {
  Content,
  Survey,
  PaginatedResponse,
  SearchRequest,
} from '@/types/admin';

// Content Management Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const ADMIN_API_BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL || '/api/content-management';

const adminApiService = createApiService(ADMIN_API_BASE_URL);

// Content Management APIs
export const contentApi = {
  // Create content (multipart form data)
  create: async (formData: FormData): Promise<Content> => {
    return adminApiService.post<Content>('/api/v1/contents', formData, {
      headers: {}, // Let browser set Content-Type for multipart
    });
  },

  // Get content by ID
  getById: async (id: string): Promise<Content> => {
    return adminApiService.get<Content>(`/api/v1/contents/${id}`);
  },

  /**
   * Upload profile picture (dedicated endpoint).
   * POST /api/v1/contents/profile-picture — multipart, requires user JWT (ROLE_USER or ROLE_ADMIN).
   * Rate limit: 10/min per user. Returns { id, fileUrl, ... }.
   */
  uploadProfilePicture: async (formData: FormData): Promise<Content> => {
    return adminApiService.post<Content>('/api/v1/contents/profile-picture', formData, {
      headers: {},
    });
  },

  /**
   * Get profile picture by content ID for display (public, no auth).
   * GET /api/v1/contents/profile-picture/{id} — returns { fileUrl, ... }.
   */
  getProfilePicture: async (id: string): Promise<Content> => {
    return adminApiService.get<Content>(`/api/v1/contents/profile-picture/${id}`, {
      skipAuth: true,
    });
  },

  // Get content by business contentId
  getByContentId: async (contentId: string): Promise<Content> => {
    return adminApiService.get<Content>(`/api/v1/contents/content-id/${contentId}`);
  },

  // Update content metadata (not file)
  update: async (id: string, data: Partial<Content>): Promise<Content> => {
    return adminApiService.put<Content>(`/api/v1/contents/${id}`, data);
  },

  // Delete content (soft delete)
  delete: async (id: string): Promise<void> => {
    return adminApiService.delete<void>(`/api/v1/contents/${id}`);
  },

  // List contents with pagination (uses search endpoint as there's no dedicated list endpoint)
  list: async (params?: {
    page?: number;
    size?: number;
    sort?: string;
    type?: string;
    status?: string;
    category?: string;
  }): Promise<PaginatedResponse<Content>> => {
    // Use search endpoint with empty search term to list all contents
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.type) {
      // types parameter expects an array
      queryParams.append('types', params.type);
    }
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);
    // Sort parameters
    if (params?.sort) {
      const [sortBy, sortDirection] = params.sort.split(',');
      if (sortBy) queryParams.append('sortBy', sortBy || 'createdAt');
      if (sortDirection) queryParams.append('sortDirection', sortDirection || 'desc');
    } else {
      queryParams.append('sortBy', 'createdAt');
      queryParams.append('sortDirection', 'desc');
    }

    const query = queryParams.toString();
    return adminApiService.get<PaginatedResponse<Content>>(
      `/api/v1/contents/search${query ? `?${query}` : ''}`
    );
  },

  // Search contents (GET request with query parameters)
  search: async (request: SearchRequest): Promise<PaginatedResponse<Content>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('searchTerm', request.query);
    if (request.filters) {
      const filters = request.filters as Record<string, unknown>;
      if (filters.status) queryParams.append('status', String(filters.status));
      if (filters.types) {
        if (Array.isArray(filters.types)) {
          (filters.types as string[]).forEach((type: string) => queryParams.append('types', type));
        }
      }
      if (filters.tags) {
        if (Array.isArray(filters.tags)) {
          (filters.tags as string[]).forEach((tag: string) => queryParams.append('tags', tag));
        }
      }
      if (filters.category) queryParams.append('category', String(filters.category));
      if (filters.createdBy) queryParams.append('createdBy', String(filters.createdBy));
      if (filters.featured !== undefined && filters.featured !== null) queryParams.append('featured', String(filters.featured));
    }
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());
    if (request.sort) {
      // Parse sort string (e.g., "createdAt,desc" or "title,asc")
      const [sortBy, sortDirection] = request.sort.split(',');
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortDirection) queryParams.append('sortDirection', sortDirection);
    }

    const query = queryParams.toString();
    return adminApiService.get<PaginatedResponse<Content>>(
      `/api/v1/contents/search${query ? `?${query}` : ''}`
    );
  },
};

// Survey Management APIs
export const surveyApi = {
  // Create survey
  create: async (data: Omit<Survey, 'id' | 'createdAt' | 'updatedAt'>): Promise<Survey> => {
    return adminApiService.post<Survey>('/api/v1/surveys', data);
  },

  // Get survey by ID
  getById: async (id: string): Promise<Survey> => {
    return adminApiService.get<Survey>(`/api/v1/surveys/${id}`);
  },

  // Get survey by business ID (surveyId)
  getBySurveyId: async (surveyId: string): Promise<Survey> => {
    return adminApiService.get<Survey>(`/api/v1/surveys/surveyId/${surveyId}`);
  },

  // Update survey
  update: async (id: string, data: Partial<Survey>): Promise<Survey> => {
    return adminApiService.put<Survey>(`/api/v1/surveys/${id}`, data);
  },

  // Delete survey (soft delete)
  delete: async (id: string): Promise<void> => {
    return adminApiService.delete<void>(`/api/v1/surveys/${id}`);
  },

  // List surveys with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sort?: string;
    type?: string;
    status?: string;
    category?: string;
  }): Promise<PaginatedResponse<Survey>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.category) queryParams.append('category', params.category);

    const query = queryParams.toString();
    const response = await adminApiService.get<PaginatedResponse<Survey>>(
      `/api/v1/surveys${query ? `?${query}` : ''}`
    );
    return response;
  },

  // List surveys by status
  listByStatus: async (status: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Survey>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    return adminApiService.get<PaginatedResponse<Survey>>(
      `/api/v1/surveys/status/${status}${query ? `?${query}` : ''}`
    );
  },

  // List surveys by type
  listByType: async (type: string, params?: { page?: number; size?: number }): Promise<PaginatedResponse<Survey>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    return adminApiService.get<PaginatedResponse<Survey>>(
      `/api/v1/surveys/type/${type}${query ? `?${query}` : ''}`
    );
  },

  // Publish survey
  publish: async (id: string): Promise<Survey> => {
    return adminApiService.post<Survey>(`/api/v1/surveys/${id}/publish`, {});
  },

  // Archive survey
  archive: async (id: string): Promise<Survey> => {
    return adminApiService.post<Survey>(`/api/v1/surveys/${id}/archive`, {});
  },

  // Validate survey
  validate: async (id: string): Promise<{ valid: boolean; errors?: string[] }> => {
    return adminApiService.post<{ valid: boolean; errors?: string[] }>(`/api/v1/surveys/${id}/validate`, {});
  },

  // Add question to survey
  addQuestion: async (surveyId: string, question: Omit<Survey['questions'][0], 'id'>): Promise<Survey> => {
    return adminApiService.post<Survey>(`/api/v1/surveys/${surveyId}/questions`, question);
  },

  // Update question in survey
  updateQuestion: async (surveyId: string, questionId: string, question: Partial<Survey['questions'][0]>): Promise<Survey> => {
    return adminApiService.put<Survey>(`/api/v1/surveys/${surveyId}/questions/${questionId}`, question);
  },

  // Delete question from survey
  deleteQuestion: async (surveyId: string, questionId: string): Promise<void> => {
    return adminApiService.delete<void>(`/api/v1/surveys/${surveyId}/questions/${questionId}`);
  },
};

export { adminApiService };
export type { ApiError };


